import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ROUTES, SECTION } from '../routes.js';
import { scrollToHash } from '../lib/scrollToHash.js';
import BeforeAfter from '../components/BeforeAfter.jsx';
import './Hero.css';

/**
 * SLIDES – jedes Slide ist ein Vorher/Nachher-Paar.
 *
 * `before` → das Objekt im Zustand "verlassen / sanierungsbedürftig"
 * `after`  → Renovierungs- / Wunschergebnis
 */
const SLIDES = [
  {
    before: `${process.env.PUBLIC_URL}/img/abando1.webp`,
    after: `${process.env.PUBLIC_URL}/img/abando1_after.webp`,
    altBefore: 'Objekt vor Sanierung – Beispiel 1',
    altAfter: 'Objekt nach Sanierung – Beispiel 1',
    location: 'Herrenhaus · waldnahe Lage',
    detail: 'Sanierungsobjekt · Altbestand',
  },
  {
    before: `${process.env.PUBLIC_URL}/img/abando2.webp`,
    after: `${process.env.PUBLIC_URL}/img/abando2_after.webp`,
    altBefore: 'Objekt vor Sanierung – Beispiel 2',
    altAfter: 'Objekt nach Sanierung – Beispiel 2',
    location: 'Küstenvilla · Meerblick',
    detail: 'Entkernungsobjekt · mit Grund',
  },
  {
    before: `${process.env.PUBLIC_URL}/img/abando3.webp`,
    after: `${process.env.PUBLIC_URL}/img/abando3_after.webp`,
    altBefore: 'Objekt vor Sanierung – Beispiel 3',
    altAfter: 'Objekt nach Sanierung – Beispiel 3',
    location: 'Seehaus · Alpenrandlage',
    detail: 'Renovierungsobjekt · Seenähe',
  },
  {
    before: `${process.env.PUBLIC_URL}/img/abando4.webp`,
    after: `${process.env.PUBLIC_URL}/img/abando4_after.webp`,
    altBefore: 'Objekt vor Sanierung – Beispiel 4',
    altAfter: 'Objekt nach Sanierung – Beispiel 4',
    location: 'Anwesen · Poolbereich',
    detail: 'Sanierungsobjekt · großer Grund',
  },
];

const BULLETS = [
  '20–40 % unter Marktpreis',
  'Direkter Draht zum Eigentümer – ohne Maklerbüro',
  'Kein Bieterkampf. Sie sind der Einzige, der weiß, dass es diesen Deal gibt.',
];

export default function Hero() {
  const [index, setIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const intervalRef = useRef(null);
  const { pathname } = useLocation();

  const handleAnchor = (e, hash) => {
    if (pathname !== ROUTES.home) return;
    e.preventDefault();
    scrollToHash(hash);
    window.history.replaceState(null, '', `#${hash}`);
  };

  useEffect(() => {
    if (!isDragging) startAuto();
    return stopAuto;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging]);

  const startAuto = () => {
    stopAuto();
    intervalRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % SLIDES.length);
    }, 6500);
  };

  const stopAuto = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
  };

  const goTo = (i) => {
    setIndex(i);
    startAuto();
  };

  const next = () => goTo((index + 1) % SLIDES.length);
  const prev = () => goTo((index - 1 + SLIDES.length) % SLIDES.length);

  return (
    <section className="hero" aria-label="Pre Market Scouting">
      <div className="hero__inner">
        <div className="hero__grid">
        {/* LEFT 40% – text */}
        <div className="hero__text">
          <div className="hero__text-inner">
            <span className="eyebrow hero__eyebrow">
              <svg
                className="hero__eyebrow-icon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <polyline points="9 12 11 14 15 10" />
              </svg>
              100 % auf Käuferseite · diskret
            </span>

            <h1 className="hero__headline">
              Wir finden Ihre{' '}
              <span className="hero__headline-accent">Traumimmobilie</span>.
              <br />
              <span className="hero__headline-sub">
                Off Market. Ohne Makler.
              </span>
            </h1>

            <p className="hero__lead">
              Wir finden Immobilien, bevor der Markt sie sieht – und bringen Sie
              direkt zum Eigentümer.
            </p>

            <ul className="hero__bullets">
              {BULLETS.map((b, i) => (
                <li key={i}>
                  <span className="hero__check" aria-hidden="true">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="hero__actions">
              <Link
                to={`${ROUTES.home}#${SECTION.kontakt}`}
                onClick={(e) => handleAnchor(e, SECTION.kontakt)}
                className="btn-primary hero__cta"
              >
                Jetzt Region anfragen
                <svg className="arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="13 6 19 12 13 18" />
                </svg>
              </Link>
              <div className="hero__trust">
                <div className="hero__avatars" aria-hidden="true">
                  <span style={{ backgroundImage: `url(${SLIDES[0].after})` }} />
                  <span style={{ backgroundImage: `url(${SLIDES[1].after})` }} />
                  <span style={{ backgroundImage: `url(${SLIDES[2].after})` }} />
                </div>
                <div className="hero__trust-text">
                  <strong>3000+ renovierungsbedürftige Objekte</strong>
                  <span>Off-Market · in unserer Datenbank</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT 60% – slider */}
        <div className="hero__media" onMouseEnter={stopAuto} onMouseLeave={startAuto}>
          <div className="hero__slider" aria-roledescription="carousel">
            {SLIDES.map((s, i) => (
              <figure
                key={s.before}
                className={`hero__slide ${i === index ? 'is-active' : ''}`}
                aria-hidden={i !== index}
              >
                <BeforeAfter
                  before={s.before}
                  after={s.after}
                  beforeAlt={s.altBefore}
                  afterAlt={s.altAfter}
                  onDragStart={() => setIsDragging(true)}
                  onDragEnd={() => setIsDragging(false)}
                />
                <figcaption className="hero__slide-caption">
                  <span className="hero__slide-location">{s.location}</span>
                  <span className="hero__slide-detail">{s.detail}</span>
                </figcaption>
              </figure>
            ))}

            <button
              className="hero__arrow hero__arrow--prev"
              onClick={prev}
              aria-label="Vorheriges Objekt"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              className="hero__arrow hero__arrow--next"
              onClick={next}
              aria-label="Nächstes Objekt"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>

            <div className="hero__dots" role="tablist">
              {SLIDES.map((_, i) => (
                <button
                  key={i}
                  role="tab"
                  aria-selected={i === index}
                  aria-label={`Objekt ${i + 1}`}
                  className={`hero__dot ${i === index ? 'is-active' : ''}`}
                  onClick={() => goTo(i)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
      </div>
    </section>
  );
}
